import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import AnimatedBackdrop from "@/components/AnimatedBackdrop";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("category") || "All";
  const [gridRef, gridRevealed] = useReveal({ threshold: 0.05 });

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products));
    api.get("/products/categories").then((r) => setCats(r.data.categories));
  }, []);

  const filtered = useMemo(() => {
    if (activeCat === "All") return products;
    return products.filter((p) => p.category === activeCat);
  }, [products, activeCat]);

  return (
    <div data-testid="shop-page" className="pt-32">
      <section className="relative max-w-[1440px] mx-auto px-6 lg:px-12 overflow-hidden">
        <AnimatedBackdrop variant="cream" />
        <div className="relative z-10">
        <div className="overline tt-fade-up">The Collection · {filtered.length} Pieces</div>
        <h1 className="font-serif-editorial text-5xl lg:text-7xl mt-6 leading-none tt-fade-up" style={{ animationDelay: "150ms" }}>Equipment.</h1>

        {/* Filter row */}
        <div className="mt-12 pb-6 border-b border-[#E5E5E5] flex flex-wrap gap-x-8 gap-y-3 tt-fade-up" style={{ animationDelay: "300ms" }}>
          <button
            data-testid="filter-all"
            onClick={() => setParams({})}
            className={`overline transition-colors duration-300 ${activeCat === "All" ? "text-[#1C1C1C]" : "text-[#A8A096] hover:text-[#1C1C1C]"}`}
          >All</button>
          {cats.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.replace(/\s+/g, "-")}`}
              onClick={() => setParams({ category: c })}
              className={`overline transition-colors duration-300 ${activeCat === c ? "text-[#1C1C1C]" : "text-[#A8A096] hover:text-[#1C1C1C]"}`}
            >{c}</button>
          ))}
        </div>

        {/* Grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-16 mt-16 mb-32">
          {filtered.map((p, i) => {
            const isExternal = !!p.external_url;
            const commonProps = {
              "data-testid": `product-card-${p.slug}`,
              className: `group block tt-reveal tt-card-lift ${gridRevealed ? "is-revealed" : ""}`,
              style: { transitionDelay: gridRevealed ? `${i * 80}ms` : "0ms" },
            };
            const CardInner = (
              <>
              <div className="aspect-[4/5] bg-white overflow-hidden border border-[#E5E5E5] relative">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[900ms] ease-out" />
                <div className="absolute inset-0 bg-[#1C1C1C]/0 group-hover:bg-[#1C1C1C]/5 transition-colors duration-500 pointer-events-none" />
                <div className="absolute top-0 left-0 w-0 h-px bg-[#C9A961] group-hover:w-full transition-all duration-700 ease-out pointer-events-none" />
              </div>
              <div className="mt-6 flex justify-between items-start gap-4">
                <div>
                  <div className="overline group-hover:text-[#1C1C1C] transition-colors duration-300">{p.category}</div>
                  <div className="font-serif-editorial text-2xl mt-2 leading-tight">{p.name}</div>
                  <div className="text-sm text-[#5C5C5C] mt-2">{p.tagline}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm">{fmt(p.price)}</div>
                  <ArrowUpRight size={16} strokeWidth={1.25} className="mt-4 ml-auto group-hover:rotate-45 group-hover:scale-110 transition-transform duration-500" />
                </div>
              </div>
              </>
            );
            return isExternal ? (
              <a
                key={p.slug}
                href={p.external_url}
                target="_blank"
                rel="noopener noreferrer"
                {...commonProps}
              >
                {CardInner}
              </a>
            ) : (
              <Link
                key={p.slug}
                to={`/product/${p.slug}`}
                {...commonProps}
              >
                {CardInner}
              </Link>
            );
          })}
        </div>
        </div>
      </section>
    </div>
  );
}
